/**
 * ShiptimizeState — Single source of truth for PUDO checkout state.
 *
 * Replaces the scattered state pattern where the same data lived in:
 *   window.carrier_id, #shipping_carrier_id DOM field, WC session,
 *   localStorage, widget class property, map interface, and extracted on-demand.
 *
 * Rules:
 *   - Only this module writes to #shipping_carrier_id, #shipping_pickup_id, etc.
 *   - Only this module writes to localStorage pickup keys.
 *   - Everything else calls state.get() / state.set() / state.onChange().
 *
 * Usage:
 *   import ShiptimizeState, { PICKUP_BEHAVIOUR, UI_STATUS } from './state/shiptimize-state.js';
 *
 *   const state = new ShiptimizeState(ajaxUrl, nonce);
 *   state.init(window.shiptimizeInitialState);   // bootstrapped by PHP
 *
 *   state.onChange((newState) => {
 *     // react to any state change
 *   });
 *
 *   state.set('carrierId', 70);
 *   state.set('pickupPoint', { id: 'PT001', label: 'Shop A', extended: '' });
 *   state.clearPickup();
 */

// ── Constants ──────────────────────────────────────────────────────────────────

export const PICKUP_BEHAVIOUR = {
  OPTIONAL:   0,   // Customer may select a pickup point but is not required to
  MANDATORY:  1,   // Customer must select a pickup point before checkout
  IMPOSSIBLE: 2,   // Carrier does not support pickup — reject any selected point
};

export const UI_STATUS = {
  IDLE:     'idle',      // No pickup point selected, nothing pending
  LOADING:  'loading',   // Fetching pickup points from API
  ERROR:    'error',     // API call failed or validation error
  SELECTED: 'selected',  // Customer has selected a pickup point
};

// ── Class ──────────────────────────────────────────────────────────────────────

export default class ShiptimizeState {
  /**
   * @param {string} ajaxUrl  WordPress admin-ajax.php URL
   * @param {string} nonce    shiptimize_vars.nonce for AJAX requests
   */
  constructor(ajaxUrl, nonce) {
    this._ajaxUrl = ajaxUrl;
    this._nonce   = nonce;

    this._state = {
      carrierId:       0,
      checkoutType:    'unknown',              // 'classic' | 'block' | 'unknown'
      pickupBehaviour: PICKUP_BEHAVIOUR.OPTIONAL,
      pickupPoint:     null,                   // { id, label, extended } | null
      address:         null,                   // { Streetname1, City, PostalCode, Country, State } | null
      uiStatus:        UI_STATUS.IDLE,
    };

    this._subscribers = [];
    this._syncTimer   = null;
    this._syncPending = false;
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  /**
   * Bootstrap from PHP-injected data (set by inject_checkout_state() via wp_localize_script).
   * Call once on page load before anything else reads from state.
   *
   * @param {object} serverState  The object from window.shiptimizeInitialState
   */
  init(serverState = {}) {
    if (serverState.carrierId !== undefined) {
      this._state.carrierId = parseInt(serverState.carrierId, 10) || 0;
    }
    if (serverState.pickupBehaviour !== undefined) {
      this._state.pickupBehaviour = parseInt(serverState.pickupBehaviour, 10);
    }
    if (serverState.pickupPoint) {
      this._state.pickupPoint = serverState.pickupPoint;
    }
    if (serverState.checkoutType) {
      this._state.checkoutType = serverState.checkoutType;
    }

    // Bring form fields into sync with server state immediately
    this._syncToFormFields();
  }

  // ── Read ──────────────────────────────────────────────────────────────────────

  /**
   * Read a single state value.
   * @param {string} key
   */
  get(key) {
    return this._state[key];
  }

  /**
   * Read the full state as a shallow copy (safe to pass around).
   */
  getAll() {
    return { ...this._state };
  }

  // ── Write ─────────────────────────────────────────────────────────────────────

  /**
   * Set a single state value. Triggers subscriber notifications and
   * schedules a debounced server sync.
   *
   * @param {string} key
   * @param {*}      value
   */
  set(key, value) {
    if (!(key in this._state)) {
      console.warn(`ShiptimizeState: unknown key "${key}" — ignored`);
      return;
    }

    const prev = this._state[key];
    this._state[key] = value;

    // Side effects per key
    if (key === 'carrierId') {
      this._syncToFormFields();
    }

    if (key === 'pickupPoint') {
      this._syncToFormFields();
      this._state.uiStatus = value !== null ? UI_STATUS.SELECTED : UI_STATUS.IDLE;
    }

    if (key === 'address' && this._hasAddressChanged(prev, value)) {
      // Address changed — drop any picked point and reset UI
      this._state.pickupPoint = null;
      this._state.uiStatus    = UI_STATUS.IDLE;
      this._syncToFormFields();
    }

    this._notifySubscribers();
    this._scheduleSyncToServer();
  }

  /**
   * Clear the selected pickup point and reset UI status.
   * Use this when the carrier changes, address changes, or user deselects.
   */
  clearPickup() {
    this._state.pickupPoint = null;
    this._state.uiStatus    = UI_STATUS.IDLE;
    this._syncToFormFields();
    this._notifySubscribers();
    this._scheduleSyncToServer();
  }

  clearCarrier() {
    this._state.carrierId   = 0;
    this._state.pickupPoint = null;
    this._state.uiStatus    = UI_STATUS.IDLE;
    this._syncToFormFields();
    this._notifySubscribers();
    // Sync immediately — not debounced — so WC session is cleared before the
    // user can submit. Debounced sync risks a race condition where the old
    // pickup point is still in session if the user submits within 300ms.
    if (this._syncTimer) clearTimeout(this._syncTimer);
    this._syncToServer();
  }

  /**
   * Full reset — call after order is placed or session is cleared.
   * Preserves checkoutType since that doesn't change mid-session.
   */
  reset() {
    this._state = {
      carrierId:       0,
      checkoutType:    this._state.checkoutType,
      pickupBehaviour: PICKUP_BEHAVIOUR.OPTIONAL,
      pickupPoint:     null,
      address:         null,
      uiStatus:        UI_STATUS.IDLE,
    };
    this._syncToFormFields();
    this._notifySubscribers();
  }

  // ── Subscriptions ─────────────────────────────────────────────────────────────

  /**
   * Subscribe to state changes.
   * The callback receives a snapshot of the full state object.
   * Returns an unsubscribe function — call it to stop receiving updates.
   *
   * @param   {function} callback  fn(stateSnapshot)
   * @returns {function}           unsubscribe
   */
  onChange(callback) {
    this._subscribers.push(callback);
    return () => {
      this._subscribers = this._subscribers.filter((cb) => cb !== callback);
    };
  }

  // ── Form field sync ───────────────────────────────────────────────────────────

  /**
   * Write current state values into checkout hidden fields.
   * This is the ONLY place in the codebase that should write to these fields.
   */
  _syncToFormFields() {
    const { carrierId, pickupPoint } = this._state;

    this._setField('#shipping_carrier_id',      carrierId                        || '');
    this._setField('#shipping_pickup_id',       pickupPoint ? pickupPoint.id       : '');
    this._setField('#shipping_pickup_label',    pickupPoint ? pickupPoint.label    : '');
    this._setField('#shipping_pickup_extended', pickupPoint ? pickupPoint.extended : '');
  }

  _setField(selector, value) {
    const el = document.querySelector(selector);
    if (el) {
      el.value = value;
    }
  }

  // ── Server sync ───────────────────────────────────────────────────────────────

  /**
   * Debounce server sync — waits 300ms after the last set() call.
   * Prevents a flood of AJAX requests when multiple values change quickly.
   */
  _scheduleSyncToServer() {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
    }
    this._syncTimer = setTimeout(() => this._syncToServer(), 300);
  }

  /**
   * Send current pickup state to the server so WC session stays in sync.
   * One request at a time — if one is in flight, the next waits for it.
   */
  _syncToServer() {
    if (this._syncPending) {
      // Re-schedule so the update isn't lost
      this._syncTimer = setTimeout(() => this._syncToServer(), 100);
      return;
    }

    this._syncPending = true;
    const { carrierId, pickupPoint } = this._state;

    jQuery.post(
      this._ajaxUrl,
      {
        action:                     'shiptimize_set_pickup_point',
        shipping_carrier_id:        carrierId                        || '',
        shipping_pickup_id:         pickupPoint ? pickupPoint.id       : '',
        shipping_pickup_label:      pickupPoint ? pickupPoint.label    : '',
        shiptimize_pickup_extended: pickupPoint ? pickupPoint.extended : '',
        shiptimize_nonce:           this._nonce,
      },
      (response) => {
        this._syncPending = false;
        if (!response || !response.success) {
          console.warn('ShiptimizeState: server sync returned failure', response);
        }
      },
    ).fail(() => {
      this._syncPending = false;
      console.warn('ShiptimizeState: server sync AJAX request failed');
    });
  }

  // ── Internal helpers ──────────────────────────────────────────────────────────

  _notifySubscribers() {
    const snapshot = this.getAll();
    for (const cb of this._subscribers) {
      try {
        cb(snapshot);
      } catch (e) {
        console.error('ShiptimizeState subscriber threw an error:', e);
      }
    }
  }

  /**
   * Compare two address objects — only the fields that matter for pickup point lookup.
   */
  _hasAddressChanged(prev, next) {
    if (!prev && !next) return false;
    if (!prev || !next) return true;
    return (
      prev.PostalCode  !== next.PostalCode  ||
      prev.Country     !== next.Country     ||
      prev.City        !== next.City        ||
      prev.Streetname1 !== next.Streetname1
    );
  }
}
